/**
 * Polymorphic credential helpers (ADR-CMRC-013, Fase 3).
 *
 * Mirror of the backend's CredentialResolver.detect(): one input accepts a
 * redemption code (UUID), a CFDI folio fiscal (also UUID — disambiguated
 * server-side by probe order) or a quote folio ("RV-1024" / "1024").
 * Client-side detection is for UX (icon + hint + early validation) only;
 * the backend is the source of truth.
 */

export type CredentialKind = "uuid" | "quote" | "folio" | "unknown";

const UUID_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const QUOTE_RX = /^[A-Za-z][A-Za-z0-9]{0,9}-\d{1,10}$/;
const FOLIO_RX = /^\d{1,10}$/;
/** Prefix of a UUID still being typed/pasted — don't flag as error yet. */
const UUID_PARTIAL_RX = /^[0-9a-f]{1,8}(-[0-9a-f]{0,4}){0,4}$/i;

export function detectCredential(raw: string): CredentialKind {
  const c = raw.trim();
  if (!c) return "unknown";
  if (UUID_RX.test(c)) return "uuid";
  if (QUOTE_RX.test(c)) return "quote";
  if (FOLIO_RX.test(c)) return "folio";
  return "unknown";
}

/** True while the value could still become valid (suppress premature errors). */
export function isPartialCredential(raw: string): boolean {
  const c = raw.trim();
  if (!c) return true;
  if (UUID_PARTIAL_RX.test(c)) return true;
  if (/^[A-Za-z][A-Za-z0-9]{0,9}-?\d{0,10}$/.test(c)) return true;
  return false;
}

export const KIND_META: Record<
  Exclude<CredentialKind, "unknown">,
  { icon: "ticket" | "receipt" | "clipboard"; label: string; sub: string }
> = {
  uuid: {
    icon: "ticket",
    label: "Código o folio fiscal",
    sub: "Código de activación o CFDI (folio fiscal)",
  },
  quote: {
    icon: "clipboard",
    label: "Cotización",
    sub: "Folio de cotización (serie-folio)",
  },
  folio: {
    icon: "clipboard",
    label: "Cotización",
    sub: "Folio de cotización",
  },
};

/** Post-redeem: el backend ya nos dijo qué era. */
export const SOURCE_TYPE_META: Record<string, { label: string; sub: string }> = {
  redemption: { label: "Código de activación", sub: "Canje de código" },
  cfdi: { label: "Factura (CFDI)", sub: "Activación desde folio fiscal" },
  quote: { label: "Cotización", sub: "Activación desde cotización" },
};

/**
 * errorCode del backend → mensaje contextual (Fase 3 requirement).
 * Cualquier código no mapeado cae al mensaje genérico del caller.
 */
export const CREDENTIAL_ERROR_MESSAGES: Record<string, string> = {
  CREDENTIAL_FORMAT:
    "No reconocemos ese formato. Pega un código de activación, el folio fiscal de tu factura (UUID) o el folio de tu cotización (ej. RV-1024).",
  CREDENTIAL_NOT_FOUND:
    "No encontramos un código, factura o cotización con ese valor. Revisa que esté completo.",
  REDEMPTION_NOT_AVAILABLE: "Este código ya fue usado, expiró o fue revocado.",
  CREDENTIAL_EXHAUSTED:
    "Esta factura ya no tiene licencias disponibles para activar.",
  CFDI_NOT_STAMPED: "La factura existe pero aún no está timbrada.",
  CFDI_CANCELLED: "Esa factura está cancelada ante el SAT.",
  CFDI_WRONG_TYPE: "Solo facturas de tipo Ingreso pueden activar licencias.",
  CFDI_NO_LICENSING_ITEMS:
    "Esa factura no contiene productos de licenciamiento Synapta.",
  QUOTE_NOT_REDEEMABLE:
    "La cotización aún no está aprobada. Pide a tu asesor que la apruebe e intenta de nuevo.",
  QUOTE_ALREADY_REDEEMED: "Esa cotización ya fue canjeada.",
  QUOTE_AMBIGUOUS:
    "Hay más de una cotización con ese folio. Usa el formato SERIE-FOLIO (ej. RV-1024).",
  CREDENTIAL_BACKEND_DOWN:
    "El servicio de validación no está disponible en este momento. Intenta en unos minutos.",
  CREDENTIAL_BACKEND_CONFIG:
    "El servicio de validación no está disponible en este momento. Intenta en unos minutos.",
};

export function credentialErrorMessage(errorCode: string | undefined | null): string | null {
  if (!errorCode) return null;
  return CREDENTIAL_ERROR_MESSAGES[errorCode] || null;
}
